// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Anchor, Box, Button, Checkbox, Divider, Group, Modal, Select, Stack, Text } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { useMedplum } from '@medplum/react';
import type { JSX } from 'react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import zoomLogo from '../../assets/zoom-logo.png';
import type { AppointmentDetails } from '../../hooks/useAppointmentDetails';
import type { ProviderAppointmentDetails } from '../../hooks/useProviderAppointmentDetails';
import { cancelProviderAppointment } from '../../services/appointment-lifecycle-service';
import {
  migrateAppointmentToCurrentEnvironment,
  needsEnvironmentMigration,
} from '../../services/appointment-migration-service';
import { recordMissedVisitFee } from '../../services/appointment-missed-visit-fee';
import { formatAppointmentDate } from '../../utils/appointment-formatting';
import { AppointmentTimeWithTimezone } from '../appointments/AppointmentTimeWithTimezone';
import { AppointmentCancellationModal } from './AppointmentCancellationModal';

const BRAND = '#640040';
const RESOURCES_BG = 'var(--mantine-color-gray-0)';

interface AppointmentDetailsModalProps {
  opened: boolean;
  onClose: () => void;
  appointment: ProviderAppointmentDetails | AppointmentDetails;
  patientId?: string;
  patientName?: string;
  onCancelled?: () => void;
  onMigrated?: () => void;
}

interface ZoomCardProps {
  joinUrl: string;
}

function ZoomCard({ joinUrl }: ZoomCardProps): JSX.Element {
  const copyLink = (): void => {
    navigator.clipboard.writeText(joinUrl).catch(() => undefined);
  };
  return (
    <Box
      style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        width: '100%',
        maxWidth: 420,
        backgroundColor: 'white',
        border: `1px solid ${BRAND}`,
        borderRadius: 8,
        padding: '8px 16px 8px 8px',
        gap: 20,
      }}
    >
      <Box
        component="img"
        src={zoomLogo}
        alt="Zoom"
        style={{ width: 90, height: 90, borderRadius: 4, flexShrink: 0, objectFit: 'cover' }}
      />
      <Stack gap={2}>
        <Text fw={600} fz={18} lh="27px">
          Join Zoom Meeting
        </Text>
        <Anchor href={joinUrl} target="_blank" rel="noopener noreferrer" fz={16} td="underline" c={BRAND} lh="24px">
          {joinUrl}
        </Anchor>
        <Anchor fw={600} fz={16} td="underline" c={BRAND} lh="24px" onClick={copyLink}>
          Copy Link
        </Anchor>
      </Stack>
    </Box>
  );
}

export function AppointmentDetailsModal({
  opened,
  onClose,
  appointment,
  patientId: patientIdProp,
  patientName: patientNameProp,
  onCancelled,
  onMigrated,
}: AppointmentDetailsModalProps): JSX.Element {
  const medplum = useMedplum();
  const navigate = useNavigate();
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelConfirmationOpen, setCancelConfirmationOpen] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [cancelException, setCancelException] = useState<string | null>(null);
  const [waiveFee, setWaiveFee] = useState(false);
  const patientId = ('patientId' in appointment ? appointment.patientId : undefined) ?? patientIdProp;
  const patientName =
    ('patientName' in appointment ? appointment.patientName : undefined) ?? patientNameProp ?? 'Patient';
  const goTo = (path: string): void => {
    navigate(path);
    onClose();
  };
  const encounterId = appointment.encounter?.id;
  const joinCallPath =
    encounterId && patientId
      ? `/Patient/${patientId}/Encounter/${encounterId}`
      : `/Appointment/${appointment.appointment.id}/session`;
  const dateStr = formatAppointmentDate(appointment.appointment.start);
  const requiresMigration = needsEnvironmentMigration(appointment.appointment);

  const handleCancel = async (): Promise<void> => {
    const appointmentId = appointment.appointment.id;
    if (!appointmentId) {
      return;
    }
    setIsCancelling(true);
    try {
      await cancelProviderAppointment(medplum, appointmentId);
      await recordMissedVisitFee(medplum, appointment.appointment, 'cancelled', {
        waived: waiveFee,
        exception: cancelException as 'emergency' | 'hospitalization' | 'technical-failure' | undefined,
      });
      showNotification({
        color: 'green',
        title: 'Appointment cancelled',
        message: 'The appointment has been cancelled successfully.',
      });
      onClose();
      onCancelled?.();
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to cancel appointment',
      });
    } finally {
      setIsCancelling(false);
    }
  };

  const handleMigrate = async (): Promise<void> => {
    setIsMigrating(true);
    try {
      const result = await migrateAppointmentToCurrentEnvironment(medplum, appointment.appointment);
      showNotification({
        color: 'green',
        title: 'Appointment migrated',
        message: `${result.migratedResourceCount} linked resources moved to ${result.toEnvironment}.`,
      });
      onClose();
      onMigrated?.();
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Migration failed',
        message: error instanceof Error ? error.message : 'Failed to migrate appointment',
      });
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      overlayProps={{ opacity: cancelConfirmationOpen ? 0 : 0.55 }}
      withCloseButton
      closeButtonProps={{ 'aria-label': 'Close appointment details' }}
      size="xl"
      padding="xl"
      radius="md"
    >
      <Stack gap={24} align="center">
        <Stack gap={12} align="center" style={{ width: '100%', maxWidth: 675 }}>
          <Text ta="center" fw={700} fz={24} lh="32px">
            {appointment.appointmentType} with{' '}
            <Anchor
              fw={700}
              fz={24}
              td="underline"
              c={BRAND}
              onClick={() => patientId && goTo(`/Patient/${patientId}`)}
              style={{ lineHeight: '32px' }}
            >
              {patientName}
            </Anchor>
          </Text>
          <Stack gap={4} align="center">
            <Text ta="center" fw={700} fz={18} lh="24px">
              {dateStr}
            </Text>
            <AppointmentTimeWithTimezone
              start={appointment.appointment.start}
              end={appointment.appointment.end}
              groupProps={{ justify: 'center' }}
              textProps={{ fw: 700, fz: 18, lh: '24px' }}
            />
          </Stack>
        </Stack>
        {appointment.joinUrl && (
          <Box style={{ width: '100%', backgroundColor: RESOURCES_BG, borderRadius: 8, padding: '29px 20px' }}>
            <Stack gap={20} align="center">
              <ZoomCard joinUrl={appointment.joinUrl} />
            </Stack>
          </Box>
        )}
        {requiresMigration && (
          <Box style={{ width: '100%' }}>
            <Alert color="yellow" variant="light" title="Needs migration">
              This appointment belongs to a different environment and must be migrated before managing it here.
            </Alert>
            <Button mt="sm" color="yellow" fullWidth onClick={handleMigrate} loading={isMigrating}>
              Migrate
            </Button>
          </Box>
        )}
        <Stack gap={8} align="center" style={{ width: '100%' }}>
          <Text fz={16} lh="24px">
            What would you like to do?
          </Text>
          <Group justify="center" gap={10} style={{ width: '100%', maxWidth: 600 }}>
            <Button
              variant="outline"
              size="lg"
              style={{ color: BRAND, borderColor: BRAND }}
              onClick={() => goTo(`/Patient/${patientId}/Appointment/reschedule/${appointment.appointment.id}`)}
            >
              Reschedule
            </Button>
            <Button
              variant="outline"
              size="lg"
              style={{ color: BRAND, borderColor: BRAND }}
              onClick={() => goTo(joinCallPath)}
            >
              Join Call
            </Button>
          </Group>
          <Divider w="100%" />
          <Button variant="subtle" color="red" size="sm" onClick={() => setCancelConfirmationOpen(true)}>
            Cancel appointment
          </Button>
        </Stack>
      </Stack>
      <AppointmentCancellationModal
        opened={cancelConfirmationOpen}
        onClose={() => setCancelConfirmationOpen(false)}
        onConfirm={handleCancel}
        patientName={patientName}
        reasonInput={
          <>
            <Select
              label="Exception (if applicable)"
              placeholder="No exception"
              clearable
              data={[
                { value: 'emergency', label: 'Documented emergency' },
                { value: 'hospitalization', label: 'Hospitalization' },
                { value: 'technical-failure', label: 'Polaris technical failure' },
              ]}
              value={cancelException}
              onChange={setCancelException}
            />
            <Checkbox
              label="Courtesy-waive missed appointment fee"
              checked={waiveFee}
              onChange={(event) => setWaiveFee(event.currentTarget.checked)}
            />
          </>
        }
        loading={isCancelling}
      />
    </Modal>
  );
}
